import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { DigitalMaterial, DBDigitalMaterial, CreateDigitalMaterial } from '@/types/library';

// Función para transformar de snake_case a camelCase
const transformDBToClient = (item: DBDigitalMaterial): DigitalMaterial => ({
  id: item.id,
  title: item.title,
  description: item.description,
  content: item.content,
  coverImage: item.cover_image,
  type: item.type,
  fileUrl: item.file_url,
  isPremium: item.is_premium,
  category: item.category,
  tags: item.tags,
  createdAt: item.created_at,
  updatedAt: item.updated_at,
  readTime: item.read_time,
  downloadCount: item.download_count,
  isDownloadable: item.is_downloadable
});

// Cache para materiales con tiempo de expiración
interface CacheItem {
  data: DigitalMaterial;
  timestamp: number;
}

const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutos
const materialCache = new Map<string, CacheItem>();

export const useDigitalMaterials = () => {
  const [materials, setMaterials] = useState<DigitalMaterial[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const checkSession = async () => {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      throw new Error('Error de sesión: ' + sessionError.message);
    }
    if (!session) {
      throw new Error('No hay sesión activa');
    }
    return session;
  };

  const fetchMaterials = async () => {
    if (!isLoading) setIsLoading(true);
    try {
      await checkSession();
      
      const { data, error } = await supabase
        .from('digital_materials')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const transformedMaterials = data.map(transformDBToClient);
      
      // Precargar imágenes
      transformedMaterials.forEach(material => {
        if (material.coverImage) {
          const img = new Image();
          img.src = material.coverImage;
        }
      });

      setMaterials(transformedMaterials);
      setError(null);
    } catch (err) {
      console.error('Error fetching digital materials:', err);
      setError(err as Error);
      // Reintentar hasta 3 veces con un retraso exponencial
      if (retryCount < 3) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchMaterials();
        }, Math.pow(2, retryCount) * 1000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
    // Limpiar caché al desmontar
    return () => {
      materialCache.clear();
    };
  }, []);

  const getMaterial = async (id: string) => {
    try {
      // Verificar caché con tiempo de expiración
      const cachedItem = materialCache.get(id);
      if (cachedItem && Date.now() - cachedItem.timestamp < CACHE_EXPIRY) {
        return cachedItem.data;
      }

      await checkSession();

      const { data, error } = await supabase
        .from('digital_materials')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          throw new Error('Material no encontrado');
        }
        throw new Error('Error al cargar el material: ' + error.message);
      }

      if (!data) {
        throw new Error('Material no encontrado');
      }

      const transformedMaterial = transformDBToClient(data);
      
      // Precargar imagen
      if (transformedMaterial.coverImage) {
        const img = new Image();
        img.src = transformedMaterial.coverImage;
      }

      // Guardar en caché con timestamp
      materialCache.set(id, {
        data: transformedMaterial,
        timestamp: Date.now()
      });

      return transformedMaterial;
    } catch (err) {
      console.error('Error fetching material:', err);
      throw err;
    }
  };

  const createMaterial = async (material: Omit<DigitalMaterial, 'id' | 'createdAt' | 'updatedAt' | 'downloadCount'>) => {
    try {
      setIsLoading(true);
      await checkSession();

      // Validar campos requeridos
      if (!material.title || !material.description || !material.coverImage || !material.fileUrl) {
        throw new Error('Todos los campos son requeridos');
      }

      const newMaterial: CreateDigitalMaterial = {
        title: material.title,
        description: material.description,
        content: material.content || '', // Aseguramos que content no sea undefined
        cover_image: material.coverImage,
        type: material.type,
        file_url: material.fileUrl,
        is_premium: material.isPremium,
        category: material.category,
        tags: material.tags || [], // Aseguramos que tags no sea undefined
        read_time: material.readTime || '5', // Valor por defecto si no se proporciona
        is_downloadable: material.isDownloadable
      };

      const { data, error } = await supabase
        .from('digital_materials')
        .insert([newMaterial])
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw new Error('Error al crear el material: ' + error.message);
      }

      if (!data) {
        throw new Error('No se pudo crear el material');
      }

      const transformedData = transformDBToClient(data);
      setMaterials(prev => [transformedData, ...prev]);
      return transformedData;
    } catch (err) {
      console.error('Error creating material:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateMaterial = async (id: string, material: Partial<Omit<DigitalMaterial, 'id' | 'createdAt' | 'updatedAt'>>) => {
    try {
      setIsLoading(true);
      await checkSession();

      const updateData: Partial<CreateDigitalMaterial> = {
        title: material.title,
        description: material.description,
        content: material.content,
        cover_image: material.coverImage,
        type: material.type,
        file_url: material.fileUrl,
        is_premium: material.isPremium,
        category: material.category,
        tags: material.tags,
        read_time: material.readTime,
        is_downloadable: material.isDownloadable
      };

      const { data, error } = await supabase
        .from('digital_materials')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const transformedData = transformDBToClient(data);
      setMaterials(prev => prev.map(item => item.id === id ? transformedData : item));
      return transformedData;
    } catch (err) {
      console.error('Error updating material:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteMaterial = async (id: string) => {
    try {
      setIsLoading(true);
      await checkSession();

      const { error } = await supabase
        .from('digital_materials')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setMaterials(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      console.error('Error deleting material:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    materials,
    isLoading,
    error,
    fetchMaterials,
    getMaterial,
    createMaterial,
    updateMaterial,
    deleteMaterial,
  };
};
